import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  settingsApi,
  DEFAULT_CHARACTER_SHEET_SETTINGS,
  type CharacterSheetSettings,
} from "../lib/settings-api";
import type { OpenAiImageParams } from "../lib/books-api";
import {
  EMPTY_LABEL,
  OpenAiParamFields,
  extraToText,
  textToExtra,
} from "../components/OpenAiParamFields";
import { useConfirm } from "@/components/confirm-dialog";
import { AdvancedSection, ErrorState, FormSection } from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import { SaveBarProvider, useSaveBarForm } from "@/components/save-bar";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Globale Einstellungen einzelner Pipeline-Schritte — alles, was weder an ein
 * Buch gehört (das steht unter „Bücher") noch ein Prompt ist (das steht unter
 * „Prompts") noch in die .env (das braucht einen Deploy).
 */
export default function Settings() {
  return (
    <SaveBarProvider>
      <PageHeader
        title="Einstellungen"
        meta={
          <>
            Globale Einstellungen der Bild-Pipeline. Buchbezogenes steht unter{" "}
            <Link to="/books" className="text-ring hover:underline">
              Bücher
            </Link>
            , Prompt-Texte unter{" "}
            <Link to="/prompts" className="text-ring hover:underline">
              Prompts
            </Link>
            , Mailtexte unter{" "}
            <Link to="/mail-templates" className="text-ring hover:underline">
              Mailvorlagen
            </Link>
            .
          </>
        }
      />
      <CharacterSheetSettingsCard />
    </SaveBarProvider>
  );
}

/**
 * Modellparameter des Character-Sheet-Renders — das Gegenstück zu den
 * Preview- und Szenen-Karten unter „Bücher", nur global: das Sheet entsteht aus
 * dem Prompt `character_angle_sheet` und dem eingefrorenen Master-Sheet, einmal
 * pro Bestellposition und für jedes Buch identisch. Deshalb steht es hier und
 * nicht am Buch.
 */
function CharacterSheetSettingsCard() {
  const confirm = useConfirm();
  const [stored, setStored] = useState<CharacterSheetSettings | null>(null);
  const [defaults, setDefaults] = useState(DEFAULT_CHARACTER_SHEET_SETTINGS);
  const [draft, setDraft] = useState<CharacterSheetSettings>(
    DEFAULT_CHARACTER_SHEET_SETTINGS,
  );
  const [extraText, setExtraText] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoadError(null);
    settingsApi
      .characterSheet()
      .then((r) => {
        setStored(r.settings);
        setDefaults(r.defaults);
        setDraft(r.settings);
        setExtraText(extraToText(r.settings.openai.extra));
      })
      .catch((e) => setLoadError((e as Error).message));
  }, [reloadKey]);

  const next: CharacterSheetSettings = {
    ...draft,
    openai: { ...draft.openai, extra: textToExtra(extraText) },
  };
  const dirty =
    stored !== null && JSON.stringify(next) !== JSON.stringify(stored);

  function setOpenai(patch: Partial<OpenAiImageParams>) {
    setDraft((d) => ({ ...d, openai: { ...d.openai, ...patch } }));
  }

  async function save(): Promise<boolean> {
    setErr(null);
    try {
      const r = await settingsApi.saveCharacterSheet(next);
      setStored(r.settings);
      setDraft(r.settings);
      setExtraText(extraToText(r.settings.openai.extra));
      toast.success("Character-Sheet-Einstellungen gespeichert");
      return true;
    } catch (e) {
      setErr((e as Error).message);
      toast.error(`Character-Sheet: ${(e as Error).message}`);
      return false;
    }
  }

  useSaveBarForm({
    label: "Character-Sheet-Generierung",
    dirty,
    save,
    discard: () => {
      if (!stored) return;
      setDraft(stored);
      setExtraText(extraToText(stored.openai.extra));
      setErr(null);
    },
  });

  async function resetToDefaults() {
    const ok = await confirm({
      title: "Auf Standard zurücksetzen?",
      description:
        "Timeout und Modellparameter werden auf die Standardwerte gesetzt. Gespeichert wird erst mit „Speichern“.",
      confirmLabel: "Zurücksetzen",
    });
    if (!ok) return;
    setDraft(defaults);
    setExtraText(extraToText(defaults.openai.extra));
  }

  if (loadError && !stored) {
    return (
      <ErrorState
        message={loadError}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  return (
    <FormSection
      title="Character-Sheet-Generierung"
      description="Gilt für jedes Buch, einmal pro Bestellposition. Änderungen greifen ab dem nächsten Sheet-Job – auch für schon eingereihte Bestellungen."
      actions={
        <>
          {dirty && <StatusBadge tone="warning">Nicht gespeichert</StatusBadge>}
          <Button
            size="sm"
            variant="ghost"
            onClick={resetToDefaults}
            disabled={!stored}
          >
            Auf Standard zurücksetzen
          </Button>
        </>
      }
    >
      {!stored ? (
        <div className="space-y-3">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
      ) : (
        <>
          <Field className="max-w-xs">
            <FieldLabel htmlFor="sheet-timeout">Timeout (ms)</FieldLabel>
            <Input
              id="sheet-timeout"
              type="number"
              min={1000}
              step={1000}
              className="bg-card"
              value={draft.timeoutMs ?? ""}
              placeholder="z. B. 300000"
              onChange={(e) =>
                setDraft({
                  ...draft,
                  timeoutMs:
                    e.target.value === "" ? null : Number(e.target.value),
                })
              }
            />
            <FieldDescription>
              leer = globaler Wert, mindestens 300000
            </FieldDescription>
          </Field>

          <OpenAiParamFields
            value={draft.openai}
            onChange={setOpenai}
            idPrefix="sheet-openai"
            extra={extraText}
            onExtraChange={setExtraText}
          />

          {err && <p className="text-destructive">{err}</p>}

          <AdvancedSection label="Hintergrund zu diesen Einstellungen">
            <div className="space-y-2 text-muted-foreground">
              <p>
                Das Sheet ist ein Kopf-Tausch auf dem eingefrorenen Master-Sheet
                (Bild 1 = Master, Bild 2 = Originalfoto, Bild 3 =
                Kopf-Ausschnitt der Kunden-Vorschau). Der Prompt dazu steht
                unter{" "}
                <Link
                  to="/prompts?q=character_angle_sheet"
                  className="text-ring hover:underline"
                >
                  Prompts → character_angle_sheet
                </Link>
                .
              </p>
              <p>
                Aus dem Sheet liest jede spätere Szene die Identität, deshalb
                ist <code>quality=high</code> hier die teure, aber richtige
                Einstellung. <code>size</code> quadratisch lassen: Das
                Master-Sheet ist quadratisch, ein anderes Verhältnis schneidet
                Ansichten ab.
              </p>
              <p>„{EMPTY_LABEL}“ heißt, der Parameter wird weggelassen.</p>
            </div>
          </AdvancedSection>
        </>
      )}
    </FormSection>
  );
}

